import { CARE_SYMBOLS_PRINT_CSS } from './careSymbols';
import { QR_CODE_PRINT_CSS } from './qrCode';

export function replacePlaceholders(html, placeholderMap) {
  let content = html || '';
  Object.entries(placeholderMap).forEach(([placeholder, value]) => {
    content = content.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value ?? '');
  });
  return content;
}

// Chromium's print-to-PDF text layout can still collapse runs of plain space characters
// even though normal on-screen rendering (and the &nbsp; entities the editor produces)
// preserve them. Walking the live DOM and turning every space beyond the first in a run
// into a real non-breaking space character guarantees it survives PDF export too.
function normalizeMultiSpaceTextNodes(doc) {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) {
    nodes.push(node);
  }
  nodes.forEach((textNode) => {
    if (/[  ]{2,}/.test(textNode.nodeValue)) {
      textNode.nodeValue = textNode.nodeValue.replace(/[  ]{2,}/g, (run) => ' ' + ' '.repeat(run.length - 1));
    }
  });
}

function stripEmptyEdgeParagraphs(html) {
  let result = html.trim();
  let changed = true;
  while (changed) {
    changed = false;
    const leading = result.match(/^(\s*<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+/i);
    if (leading) {
      result = result.slice(leading[0].length).trimStart();
      changed = true;
    }
    const trailing = result.match(/(\s*<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/i);
    if (trailing) {
      result = result.slice(0, -trailing[0].length).trimEnd();
      changed = true;
    }
  }
  return result;
}

export function prepareHtmlForPrint(rawHtml) {
  // Keep &nbsp; as-is (don't collapse to a plain space) - the editor encodes runs of
  // typed spaces as alternating space/&nbsp; specifically so they survive HTML rendering;
  // converting them all to plain spaces here made repeated spaces collapse back to one.
  let formattedContent = rawHtml
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  formattedContent = formattedContent.replace(/\n/g, '<br>');
  formattedContent = stripEmptyEdgeParagraphs(formattedContent);
  return formattedContent;
}

const DEFAULT_PRINT_CSS = `
  body { 
    font-family: 'Times New Roman', serif; 
    margin: 20px;
    line-height: 1.3; 
    color: #333;
    font-size: 12px;
  }
  h2 { 
    color: #555; 
    font-size: 16px;
    margin-top: 20px;
    margin-bottom: 10px;
  }
  h3 { 
    color: #666; 
    font-size: 14px;
    margin-top: 15px;
    margin-bottom: 8px;
  }
  h4 { 
    color: #777; 
    font-size: 13px;
    margin-top: 12px;
    margin-bottom: 6px;
  }
  h5 { 
    color: #888; 
    font-size: 12px;
    margin-top: 10px;
    margin-bottom: 4px;
  }
  h6 { 
    color: #999; 
    font-size: 11px;
    margin-top: 8px;
    margin-bottom: 4px;
  }
  p { 
    margin: 0 0 3px 0;
    padding: 0;
    text-align: justify;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong, b { 
    font-weight: bold; 
  }
  em, i { 
    font-style: italic; 
  }
  u { 
    text-decoration: underline; 
  }
  ul, ol { 
    margin-bottom: 10px; 
    padding-left: 20px; 
  }
  li { 
    margin-bottom: 5px; 
  }
  blockquote { 
    margin: 15px 0; 
    padding-left: 15px; 
    border-left: 3px solid #007bff; 
    font-style: italic; 
  }
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .text-left { text-align: left; }
  .text-justify { text-align: justify; }
  .page-break { page-break-before: always; }
  .contract-content,
  .declaration-content { 
    margin: 0;
    padding: 0;
  }
  .contract-content > :first-child,
  .declaration-content > :first-child {
    margin-top: 0;
  }
  p:empty {
    min-height: 1em;
  }
  [data-template-font-size] h1,
  [data-template-font-size] h2,
  [data-template-font-size] h3,
  [data-template-font-size] h4,
  [data-template-font-size] h5,
  [data-template-font-size] h6 {
    font-size: inherit;
  }
  table {
    border-collapse: collapse;
    width: 100%;
  }
  table td,
  table th {
    border: none;
    padding: 2px 4px;
    vertical-align: top;
    text-align: left;
    font-weight: normal;
  }
  .contract-content img,
  .declaration-content img {
    max-width: 100%;
    height: auto;
  }
  ${CARE_SYMBOLS_PRINT_CSS}
  ${QR_CODE_PRINT_CSS}
`;

// Builds the complete print document (styles included) as a string. Split out of
// openPrintDocument so the same document can also be handed to the server to render as a PDF
// (emailing a decision) - going through one builder is what keeps the emailed PDF identical to
// what printing produces, rather than having a second copy of this CSS drift out of sync.
export function buildPrintDocumentHtml({
  title = 'Document',
  bodyHtml,
  extraCss = '',
  contentClass = 'contract-content',
  pageWidthCm,
  pageHeightCm
}) {
  const formattedContent = prepareHtmlForPrint(bodyHtml);

  if (!formattedContent.trim()) {
    throw new Error('Document content is empty');
  }

  const hasCustomPageSize =
    Number.isFinite(Number(pageWidthCm)) && Number.isFinite(Number(pageHeightCm));

  // Custom (small) page sizes are NOT declared via @page size: printers/print dialogs that
  // are fed a larger sheet (e.g. A4) than the declared page size center the small page on
  // that sheet. Instead, print at the sheet size the dialog/printer is already using, and
  // pin the label content to the top-left corner as a fixed-size, clipped box - this lands
  // top-left regardless of what paper size ends up selected.
  const pageCss = hasCustomPageSize
    ? `
  @media print {
    body { margin: 0; }
    .no-print { display: none; }
    @page {
      margin: 0;
      @top-left { content: ""; }
      @top-center { content: ""; }
      @top-right { content: ""; }
      @bottom-left { content: ""; }
      @bottom-center { content: ""; }
      @bottom-right { content: ""; }
    }
  }
  .print-label-box {
    position: absolute;
    top: 0.7cm;
    left: 0.7cm;
    width: ${Number(pageWidthCm)}cm;
    height: ${Number(pageHeightCm)}cm;
    overflow: hidden;
    box-sizing: border-box;
    padding: 0.3cm;
  }`
    : `
  @media print {
    body { margin: 0; }
    .no-print { display: none; }
    @page {
      margin: 0.5in;
      @top-left { content: ""; }
      @top-center { content: ""; }
      @top-right { content: ""; }
      @bottom-left { content: ""; }
      @bottom-center { content: ""; }
      @bottom-right { content: ""; }
    }
  }`;

  const bodyContent = hasCustomPageSize
    ? `<div class="print-label-box"><div class="${contentClass}">${formattedContent}</div></div>`
    : `<div class="${contentClass}">${formattedContent}</div>`;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          ${DEFAULT_PRINT_CSS}
          ${pageCss}
          ${extraCss}
        </style>
      </head>
      <body>
        ${bodyContent}
      </body>
    </html>
  `;
}

export function openPrintDocument(options) {
  const htmlContent = buildPrintDocumentHtml(options);

  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    throw new Error('Popup blocked. Please allow popups to print.');
  }
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  normalizeMultiSpaceTextNodes(printWindow.document);

  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
}

export function openBulkPrintDocument({
  title = 'Document',
  items,
  itemWidthCm,
  itemHeightCm,
  paperSize = 'A4',
  contentClass = 'declaration-content',
  extraCss = ''
}) {
  if (!items || items.length === 0) {
    throw new Error('No items to print');
  }

  const itemsHtml = items
    .map((html) => `<div class="bulk-print-item"><div class="${contentClass}">${prepareHtmlForPrint(html || '')}</div></div>`)
    .join('');

  const bulkCss = `
  @media print {
    body { margin: 0; }
    .no-print { display: none; }
    @page {
      size: ${paperSize};
      margin: 1cm;
      @top-left { content: ""; }
      @top-center { content: ""; }
      @top-right { content: ""; }
      @bottom-left { content: ""; }
      @bottom-center { content: ""; }
      @bottom-right { content: ""; }
    }
  }
  .bulk-print-grid {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 0.3cm;
  }
  .bulk-print-item {
    width: ${Number(itemWidthCm)}cm;
    height: ${Number(itemHeightCm)}cm;
    overflow: hidden;
    box-sizing: border-box;
    padding: 0.3cm;
    page-break-inside: avoid;
    break-inside: avoid;
  }`;

  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          ${DEFAULT_PRINT_CSS}
          ${bulkCss}
          ${extraCss}
        </style>
      </head>
      <body>
        <div class="bulk-print-grid">${itemsHtml}</div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    throw new Error('Popup blocked. Please allow popups to print.');
  }
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  normalizeMultiSpaceTextNodes(printWindow.document);

  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
}

export function buildTemplateDocumentHtml({
  title,
  templateContent,
  placeholderMap,
  contentClass = 'contract-content',
  pageWidthCm,
  pageHeightCm
}) {
  if (!templateContent) {
    throw new Error('Template has no content');
  }

  return buildPrintDocumentHtml({
    title,
    bodyHtml: replacePlaceholders(templateContent, placeholderMap),
    contentClass,
    pageWidthCm,
    pageHeightCm
  });
}

export function printTemplateDocument({
  title,
  templateContent,
  placeholderMap,
  contentClass = 'contract-content',
  pageWidthCm,
  pageHeightCm
}) {
  if (!templateContent) {
    throw new Error('Template has no content');
  }

  const filled = replacePlaceholders(templateContent, placeholderMap);
  openPrintDocument({
    title,
    bodyHtml: filled,
    contentClass,
    pageWidthCm,
    pageHeightCm
  });
}
